import {Component, HostListener, inject, Input, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Images, Product, User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {DcpSearchProductComponent} from '@app/search/dcp-search-product/dcp-search-product.component';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {CommerceConfig} from '@app/config/CommerceConfig';
import {BreakpointsConfig} from '@app/model/breakpoint.model';
import {DcpEddlSearchTrackingService} from '@app/services/analytics/EDDL/dcp-eddl-search-tracking.service';
import {DcpEddlSessionHelperService} from '@app/services/analytics/EDDL/dcp-eddl-session-helper.service';
import {EddlProductFindingMethods} from '@app/services/analytics/EDDL/models/eddl-adobe-tracking-data';
import {EddlProductViewEvent} from '@app/services/analytics/EDDL/events/eddl-product-view-event';

@UntilDestroy()
@Component({
  selector: 'dcp-product-tile',
  templateUrl: './product-tile.component.html',
  styleUrls: ['./product-tile.component.scss'],
  providers: [DcpSearchProductComponent]
})

export class ProductTileComponent {
  @Input() set product(p: Product) {
    this.productInner = p;
    this.isSapProduct = this.productUtilitiesService.isSapProduct(p);
    this.certification = this.productUtilitiesService.getCertification(p);
    this.mediumDescription = this.productUtilitiesService.getMediumDescription(p);
    this.setContent();
  }
  @Input() clickRank: number;
  public productInner: Product;
  public productImage: { url: string };
  public pdpTargetUrl: string;
  public isNeedToRedirect: boolean;
  public certification: string;
  public mediumDescription: string;
  public isSapProduct: boolean;
  public mobileVersion: boolean;
  public altText: string;

  public platformId = inject(PLATFORM_ID);
  public productUtilitiesService = inject(DcpProductUtilitiesService);
  private eddlSearchTrackingService = inject(DcpEddlSearchTrackingService);
  private eddSessionHelper = inject(DcpEddlSessionHelperService);
  private userAccountFacade = inject(UserAccountFacade);

  get product(): Product {
    return this.productInner;
  }

  setContent(): void {
    this.pdpTargetUrl = this.productUtilitiesService.getPdpTargetUrl(this.product);
    this.isNeedToRedirect = !!this.pdpTargetUrl;
    this.fixProductImageUrl();
    this.checkMobileView();
    if (this.productInner.images !== undefined) {
      this.altText = this.getAltText(this.productInner.images);
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.checkMobileView();
  }

  checkMobileView(): void {
    const windowWidthOnResize: number = window.innerWidth;
    this.mobileVersion = windowWidthOnResize <= (CommerceConfig as BreakpointsConfig).breakpoints.xs;
  }

  /**
   * Fix product image url
   */
  fixProductImageUrl(): void {
    let imgUrl: string;

    if (this.product.images) {
      for (const key in this.product.images) {
        imgUrl = this.findAttribute(this.product.images[key], 'url');
      }
    } else {
      imgUrl = ''
    }

    this.productImage = {
      url: imgUrl
    };
  }

  /**
   * Track a product tile click event through the EDDL. The method distinguishes between desktop and mobile view.
   * The difference between mobile and desktop is necessary as the cx-generic-link component doesn't propagate the
   * click event properly across all display modes.
   *
   * @param product Product that has been clicked on
   * @param mobileOnly boolean to define if this method should be called if the app is in desktop or mobile mode
   */
  eddlTrackProductClicked(product: Product, mobileOnly: boolean): void {
    // as product tile can be present on other than search page
    if (mobileOnly === this.mobileVersion && isPlatformBrowser(this.platformId) && window.location.href.match(/\/search([/?]|$)/)) {
      this.userAccountFacade.get().pipe(untilDestroyed(this))
        .subscribe((user: User) => {
          this.eddSessionHelper.updateSession({
            uniqueToken: user?.uid ?? user?.scimUserId,
            extras: [
              {
                event: EddlProductViewEvent.type,
                findingMethod: EddlProductFindingMethods.SEARCH_PAGE,
              },
            ]
          })
          this.eddlSearchTrackingService.trackSearchResultsClick(
            window.location.href, this.clickRank, product
          );
        })
    }
  }

  getURL(url: any[]): string {
    if (this.isNeedToRedirect) {
      return this.productUtilitiesService.setParamsToUrl(this.pdpTargetUrl).toString();
    }
    const originURL = this.productUtilitiesService.setParamsToUrl(window.location.origin);
    const pathname = url.slice(1).join('/');
    originURL.pathname = pathname;
    return originURL.toString();
  }


  getAltText(images: Images): string | undefined {
    let altText: string | undefined;

    for (const key in this.product.images) {
      altText = this.findAttribute(this.product.images[key], 'altText');
    }
    return altText;
  }

  findAttribute(obj: any, attr: string): string | undefined {
    if (typeof obj !== 'object' || obj === null) {
      return undefined;
    }

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (key === attr && typeof obj[key] === 'string') {
          return obj[key];
        } else if (typeof obj[key] === 'object') {
          const result = this.findAttribute(obj[key], attr);
          if (result) {
            return result;
          }
        }
      }
    }
    return undefined;
  }
}
